import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import LoginForm from './LoginForm';
import ForgotPasswordForm from './ForgotPasswordForm';
import SignUpForm from './SignUpForm';
import ResetPasswordForm from './ResetPasswordForm';
import SignUpOTPForm from './SignUpOTPForm';
import { ILoginPageProps } from '../../types/propTypes';
import { Common } from '../../utils/constants';
import SelectBox from '../../components/reactstrap-formik/SelectBox';
import images from '../../assets/images';
import { companyNameReplace } from '../../utils';
import 'moment/locale/es';
import 'moment/locale/is';
import { ISupportedLanguage } from '../../store/state/language.state';
import { LoginPageHook } from './LoginPageHook';
import SignInOTPForm from './SignInOTPForm';
import ResetPasswordWithTempPasswordForm from './ResetPasswordWithTempPasswordForm';
import SignInWithPassword from './SignInWithPassword';
import AuthenticatorOTP from './AuthenticatorOTP';
import WhatsAppOTP from './WhatsAppOTP';

const LoginPage = (props: ILoginPageProps) => {
  const parameters = new URLSearchParams(window.location.search);
  const navigate = useNavigate();

  const [isResetAuthMethod, setIsResetAuthMethod] = useState<boolean>(false);

  const {
    supportedLanguages,
    apiError,
    stage,
    colorStyles,
    language,
    email,
    password,
    phoneDetail,
    referralCode,
    privacyPolicy,
    setEmail,
    setPassword,
    setStage,
    setReferralCode,
    handleLanguageDropdown,
    handleChangeStage,
    handleChangeFormValues,
    handleDriverLogin,
  } = LoginPageHook(props);

  useEffect(() => {
    if (parameters?.get('stage') && parameters?.get('stage') !== 'undefined' && parameters?.get('stage') !== '3') {
      navigate('/login');
    }
    const urlParams = new URLSearchParams(window.location.search);
    const myReferralCode = urlParams.get('code');
    if (props.isSignUp) {
      setStage(Common.AuthStage.SIGNUP);
    }
    setReferralCode(String(myReferralCode));
    localStorage.removeItem(Common.SHOW_REFERRAL_SCREEN);
  }, []);

  const { t } = useTranslation();
  return (
    <>
      <div className="login-page container-fluid p-0">
        <div className="row h-100 no-gutters">
          <div className="col-md login-background-block">
            <a href="#" title={companyNameReplace(t<string>('login.label.logo'))} className="instavolt-logo">
              <img src={images.logoPNG} alt={companyNameReplace(t<string>('login.label.logo'))} />
            </a>
          </div>
          <div className="col-md-auto login-section">
            <div className="login-outer">
              {(supportedLanguages?.length > 0 || apiError) && (
                <div className="login-contain">
                  {(stage === Common.AuthStage.SIGNIN || stage === Common.AuthStage.SIGNUP) && (
                    <div className="language-dropdown font-poppins">
                      <SelectBox
                        styles={colorStyles}
                        name="language"
                        menuPlacement="top"
                        value={language && supportedLanguages.length > 0 ? language : ''}
                        options={supportedLanguages}
                        onChange={(e: ISupportedLanguage) => {
                          handleLanguageDropdown(e);
                        }}
                        getOptionLabel={(opt: ISupportedLanguage) => opt.name as string}
                        getOptionValue={(opt: ISupportedLanguage) => opt.id as number}
                        isClearable={false}
                        placeholder={t('login.placeholder.language')}
                        arrowSize="small"
                      />
                    </div>
                  )}
                  {stage === Common.AuthStage.SIGNIN && (
                    <LoginForm
                      onChangeStage={handleChangeStage}
                      handleSubmit={handleDriverLogin}
                      handleChangeFormValues={handleChangeFormValues}
                      setEmail={(data: string) => setEmail(data)}
                    />
                  )}
                  {stage === Common.AuthStage.FORGOT_PASSWORD && (
                    <ForgotPasswordForm
                      onChangeStage={handleChangeStage}
                      setEmail={(data: string) => setEmail(data)}
                      handleChangeFormValues={handleChangeFormValues}
                    />
                  )}
                  {stage === Common.AuthStage.CHANGE_PASSWORD_WITH_VERIFICATION_CODE && (
                    <ResetPasswordForm email={email} onChangeStage={handleChangeStage} />
                  )}
                  {stage === Common.AuthStage.RESET_PASSWORD_WITH_TEMP_PASSWORD && (
                    <ResetPasswordWithTempPasswordForm email={email} onChangeStage={handleChangeStage} />
                  )}
                  {stage === Common.AuthStage.SIGNUP && (
                    <SignUpForm
                      onChangeStage={handleChangeStage}
                      setEmail={(data: string) => setEmail(data)}
                      setPassword={(data: string) => setPassword(data)}
                      referaalCode={referralCode}
                      handleChangeFormValues={handleChangeFormValues}
                    />
                  )}
                  {stage === Common.AuthStage.SIGNUP_OTP && (
                    <SignUpOTPForm onChangeStage={handleChangeStage} email={email} />
                  )}
                  {stage === Common.AuthStage.SIGNIN_OTP && (
                    <SignInOTPForm
                      onChangeStage={handleChangeStage}
                      email={email}
                      password={password}
                      referralCode={referralCode}
                      isResetAuthMethod={isResetAuthMethod}
                    />
                  )}
                  {stage === Common.AuthStage.SIGNIN_WITHOUT_PASSKEY && (
                    <SignInWithPassword
                      email={email}
                      setPassword={(data: string) => setPassword(data)}
                      handleSubmit={handleDriverLogin}
                      onChangeStage={handleChangeStage}
                    />
                  )}
                  {stage === Common.AuthStage.SIGNIN_AUTHENTICATOR_OTP && (
                    <AuthenticatorOTP
                      email={email}
                      password={password}
                      onChangeStage={handleChangeStage}
                      setIsResetAuthMethod={setIsResetAuthMethod}
                    />
                  )}
                  {stage === Common.AuthStage.SIGNIN_WHATSAPP_OTP && (
                    <WhatsAppOTP
                      email={email}
                      password={password}
                      onChangeStage={handleChangeStage}
                      setIsResetAuthMethod={setIsResetAuthMethod}
                      phoneDetail={phoneDetail}
                    />
                  )}
                </div>
              )}
            </div>
            {privacyPolicy && privacyPolicy.privacy_policy_url ? (
              <p className="copyright font-poppins">
                {`${companyNameReplace(t('login.label.copyright'))} | `}
                <a
                  href={privacyPolicy.privacy_policy_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="privacy-policy-link"
                >
                  {privacyPolicy.title}
                </a>
              </p>
            ) : (
              <p className="copyright"> {companyNameReplace(t('login.label.copyright'))}</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
